import { Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { resolveSource } from 'services/imports/extractWithAssistant';
import type { PageLines } from 'services/imports/extractWithAssistant';
import type { ImportPage } from 'services/imports/fileToPages';
import type { LineSource } from 'services/imports/markdownContract';
import type { OcrLine } from 'services/imports/runOcr';
import { SourceBox } from 'views/imports/components/source-box';
import { SourcePageNav } from 'views/imports/components/source-page-nav';
import { SourcePreviewDialog } from 'views/imports/components/source-preview-dialog';
import { SourceZoomControls, ZOOM_LEVELS } from 'views/imports/components/source-zoom-controls';

export type SourceViewerProps = {
  pages: readonly ImportPage[];
  linesByPage: PageLines;
  selectedSource: LineSource | null;
  onSelectSource: (src: LineSource) => void;
  /** Source keys (`file|page|line`) cited by at least one data line. */
  linkedKeys?: ReadonlySet<string>;
  /** A box was clicked that no data line cites. */
  unusedSource?: boolean;
  noSourceSelected?: boolean;
  previewPageId?: string | null;
  onPageChange?: (pageId: string) => void;
  fill?: boolean;
  focusZoom?: number;
  showEnlarge?: boolean;
  showHeading?: boolean;
};

/** Viewport height (spacing units) kept for the app header, page title, viewer heading and toolbar in fill mode. */
const FILL_RESERVED_SPACING = 36;
const FIT_ZOOM = ZOOM_LEVELS[0];
/** Zoom applied when a source is selected; deliberately not one of the buttons. */
const FOCUS_ZOOM = 250;
const NO_LINES: readonly OcrLine[] = [];

/** Compared by value: every markdown edit re-parses into new src objects. */
const sourceKey = (src: LineSource): string => `${src.file}|${src.page}|${src.line}`;

type Bbox = OcrLine['bbox'];

/** Scrolls so the bbox center sits in the middle of the scroller (the browser clamps the edges). */
const centerOnBox = (scroller: HTMLDivElement, wrapper: HTMLDivElement, bbox: Bbox) => {
  const centerX = wrapper.offsetLeft + (bbox.left + bbox.width / 2) * wrapper.offsetWidth;
  const centerY = wrapper.offsetTop + (bbox.top + bbox.height / 2) * wrapper.offsetHeight;
  scroller.scrollTo(centerX - scroller.clientWidth / 2, centerY - scroller.clientHeight / 2);
};

const useStyles = makeStyles((theme: Theme) => createStyles({
  heading: {
    marginBottom: theme.spacing(1),
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  scroller: {
    position: 'relative',
    maxHeight: theme.spacing(60),
    overflow: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
  },
  fillRoot: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  fillScroller: {
    flex: '1 0 auto',
    height: `calc(100vh - ${theme.spacing(FILL_RESERVED_SPACING)})`,
    minHeight: theme.spacing(60),
    maxHeight: 'none',
  },
  wrapper: {
    position: 'relative',
  },
  image: {
    display: 'block',
    width: '100%',
    height: 'auto',
    userSelect: 'none',
  },
  caption: {
    display: 'block',
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
}));

export const SourceViewer = ({
  pages, linesByPage, selectedSource, onSelectSource, linkedKeys = new Set<string>(), unusedSource = false,
  noSourceSelected = false, previewPageId = null, onPageChange, fill = false, focusZoom = FOCUS_ZOOM, showEnlarge = true, showHeading = true,
}: SourceViewerProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const handledKeyRef = useRef<string | null>(null);
  const handledPreviewRef = useRef<string | null>(null);
  const centerPendingRef = useRef(false);
  const [pageId, setPageId] = useState<string | null>(null);
  const [zoom, setZoom] = useState<number>(FIT_ZOOM);
  const [loadedPageId, setLoadedPageId] = useState<string | null>(null);
  const [enlarged, setEnlarged] = useState(false);

  const viewablePages = useMemo(() => pages.filter((page) => !page.failed && page.imageDataUrl !== ''), [pages]);
  const resolved = useMemo(
    () => (selectedSource ? resolveSource(selectedSource, pages, linesByPage) : null),
    [selectedSource, pages, linesByPage],
  );
  const current = viewablePages.find((page) => page.id === pageId) ?? viewablePages[0] ?? null;
  const currentLines = current ? linesByPage.get(current.id) ?? NO_LINES : NO_LINES;
  const highlightedIndex = resolved && current && resolved.page.id === current.id ? resolved.line.index : null;
  const sourceMissing = noSourceSelected || (selectedSource !== null && resolved === null);
  const emptyCaptionKey = current === null ? 'imports:viewer.noPages' : null;
  let captionKey = emptyCaptionKey;
  if (unusedSource) captionKey = 'imports:viewer.unusedBox';
  else if (sourceMissing) captionKey = 'imports:viewer.noSource';
  const tabStopIndex = currentLines.findIndex((line) => line.index === highlightedIndex);
  const activeTabStop = tabStopIndex >= 0 ? tabStopIndex : 0;

  // A page picked outside the viewer opens at fit width; the echo of the viewer's own page change is skipped.
  useEffect(() => {
    if (previewPageId === null || handledPreviewRef.current === previewPageId) return;
    handledPreviewRef.current = previewPageId;
    centerPendingRef.current = false;
    setPageId(previewPageId);
    setZoom(FIT_ZOOM);
    scrollRef.current?.scrollTo(0, 0);
  }, [previewPageId]);

  // A new resolvable selection moves to its page and focus zoom; the same selection re-rendered does not.
  useEffect(() => {
    if (!selectedSource) {
      handledKeyRef.current = null;
      return;
    }
    const key = sourceKey(selectedSource);
    if (!resolved || handledKeyRef.current === key) return;
    handledKeyRef.current = key;
    handledPreviewRef.current = resolved.page.id;
    centerPendingRef.current = true;
    setPageId(resolved.page.id);
    setZoom(focusZoom);
    onPageChange?.(resolved.page.id);
  }, [resolved, selectedSource, onPageChange, focusZoom]);

  // Centering waits for the page image to load and for the zoomed layout to be painted.
  useEffect(() => {
    if (!centerPendingRef.current || !resolved || !current) return undefined;
    if (resolved.page.id !== current.id || loadedPageId !== current.id) return undefined;
    const frame = window.requestAnimationFrame(() => {
      centerPendingRef.current = false;
      if (scrollRef.current && wrapperRef.current) {
        centerOnBox(scrollRef.current, wrapperRef.current, resolved.line.bbox);
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [resolved, current, loadedPageId, zoom]);

  const handleZoom = (level: number) => {
    centerPendingRef.current = highlightedIndex !== null;
    setZoom(level);
  };

  const handlePageChange = (nextPageId: string) => {
    centerPendingRef.current = false;
    handledPreviewRef.current = nextPageId;
    setPageId(nextPageId);
    scrollRef.current?.scrollTo(0, 0);
    onPageChange?.(nextPageId);
  };

  // A box the reviewer clicked is already on screen: mark its selection as handled so the viewer does not zoom to it.
  const handleSelectLine = (line: OcrLine) => {
    if (!current) return;
    const src = { file: current.fileName, page: current.pageNumber, line: line.index };
    handledKeyRef.current = sourceKey(src);
    onSelectSource(src);
  };

  const focusBox = (index: number) => {
    const element = scrollRef.current?.querySelector<HTMLElement>(`[data-box-index="${index}"]`);
    element?.focus();
  };

  const handleNavigate = (index: number, step: number) => {
    const target = index + step;
    if (target >= 0 && target < currentLines.length) focusBox(target);
  };

  const renderViewer = (page: ImportPage) => (
    <>
      <div className={classes.toolbar}>
        {viewablePages.length > 1 ? (
          <SourcePageNav pages={viewablePages} current={page} onChange={handlePageChange} />
        ) : <span />}
        <SourceZoomControls zoom={zoom} onZoom={handleZoom} onEnlarge={showEnlarge ? () => setEnlarged(true) : undefined} />
      </div>
      <div ref={scrollRef} className={fill ? `${classes.scroller} ${classes.fillScroller}` : classes.scroller}>
        <div ref={wrapperRef} className={classes.wrapper} style={{ width: `${zoom}%` }}>
          <img
            src={page.imageDataUrl}
            alt={t('imports:pages.thumbnailAlt', { file: page.fileName, page: page.pageNumber })}
            className={classes.image}
            draggable={false}
            onLoad={() => setLoadedPageId(page.id)}
          />
          {currentLines.map((line, index) => (
            <SourceBox
              key={line.id}
              line={line}
              index={index}
              selected={line.index === highlightedIndex}
              linked={linkedKeys.has(sourceKey({ file: page.fileName, page: page.pageNumber, line: line.index }))}
              tabStop={index === activeTabStop}
              onSelect={handleSelectLine}
              onNavigate={handleNavigate}
            />
          ))}
        </div>
      </div>
      <SourcePreviewDialog page={page} open={enlarged} onClose={() => setEnlarged(false)} />
    </>
  );

  return (
    <section aria-label={t('imports:viewer.title')} className={fill ? classes.fillRoot : undefined}>
      {showHeading && (
        <Typography variant="subtitle2" component="h2" className={classes.heading}>
          {t('imports:viewer.title')}
        </Typography>
      )}
      {current && renderViewer(current)}
      {captionKey && (
        <Typography variant="caption" className={classes.caption} role="status">
          {t(captionKey)}
        </Typography>
      )}
    </section>
  );
};
